import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiRequestError } from '@/lib/api';
import { Button, ErrorState, LoadingBlock, PageHeader } from '@/components/ui';
import { useToast } from '@/lib/toast';
import { copy, useI18n } from '@/lib/i18n';
import './CalendarBooking.css';

interface Slot { startsAt: string; endsAt: string }

export default function CalendarBooking() {
  const toast = useToast();
  const { language, locale } = useI18n();
  const [duration, setDuration] = useState<30 | 60>(30);
  const [selected, setSelected] = useState<Slot | null>(null);
  const [completed, setCompleted] = useState<{ meetUrl: string | null } | null>(null);
  const range = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 13);
    const day = (value: Date) => new Date(value.getTime() + 3 * 60 * 60_000).toISOString().slice(0, 10);
    return { from: day(from), to: day(to) };
  }, []);
  const availability = useQuery({
    queryKey: ['calendar', 'availability', range, duration],
    queryFn: () => api.get<{ providerReady: boolean; slots: Slot[] }>('/calendar/availability', {
      ...range,
      durationMinutes: duration,
    }),
  });
  const book = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error(copy(language, 'Saat seçilmedi.', 'No time selected.'));
      const result = await api.post<{ meetUrl: string | null }>('/calendar/bookings', {
        title: copy(language, 'Wondeon tanışma toplantısı', 'Wondeon introduction meeting'),
        startsAt: selected.startsAt,
        durationMinutes: duration,
        createMeet: true,
        kind: 'meeting',
      });
      await api.patch('/account/onboarding/contact', { preference: 'meet' });
      return result;
    },
    onSuccess: (result) => setCompleted(result),
    onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : copy(language, 'Randevu oluşturulamadı.', 'The appointment could not be created.')),
  });
  const grouped = groupSlots(availability.data?.slots ?? []);

  if (completed) {
    return (
      <div className="meeting-success">
        <span aria-hidden="true">✓</span>
        <p className="meeting-success__eyebrow">{copy(language, 'Randevu oluşturuldu', 'Appointment created')}</p>
        <h1>{copy(language, 'Takviminize davet gönderdik.', 'We sent an invitation to your calendar.')}</h1>
        <p>{copy(language, 'Toplantı Hub takviminize ve e-posta adresinize eklendi.', 'The meeting was added to your Hub calendar and sent to your email address.')}</p>
        <div className="meeting-success__actions">
          {completed.meetUrl && <a className="btn btn--primary btn--md" href={completed.meetUrl} target="_blank" rel="noreferrer">{copy(language, 'Meet bağlantısını aç', 'Open Meet link')}</a>}
          <Link className="btn btn--secondary btn--md" to="/">{copy(language, 'Ana sayfaya dön', 'Back to home')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-page stack stack--lg">
      <PageHeader title={copy(language, 'Tanışma toplantısı', 'Introduction meeting')} description={copy(language, 'Wondeon ekibinin uygun saatlerinden birini seçin. Başlangıca son 30 dakika kala randevu alınamaz.', 'Choose one of the Wondeon team’s available times. Appointments cannot be booked within 30 minutes of the start time.')} />
      <div className="meeting-page__layout">
        <aside className="meeting-page__summary">
          <span className="meeting-page__mark">W</span>
          <h2>{copy(language, 'Wondeon ekibi', 'Wondeon team')}</h2>
          <p>{copy(language, 'Kurumunuz, kullanım alanınız ve ilgilendiğiniz deneyim hakkında kısa bir görüşme.', 'A short conversation about your organization, use case and the experience you are interested in.')}</p>
          <dl><div><dt>{copy(language, 'Süre', 'Duration')}</dt><dd>{duration} {copy(language, 'dakika', 'minutes')}</dd></div><div><dt>{copy(language, 'Saat dilimi', 'Time zone')}</dt><dd>Istanbul</dd></div><div><dt>{copy(language, 'Görüşme', 'Meeting')}</dt><dd>Google Meet</dd></div></dl>
          <div className="meeting-page__duration" aria-label={copy(language, 'Toplantı süresi', 'Meeting duration')}>
            <button type="button" className={duration === 30 ? 'is-active' : ''} onClick={() => { setDuration(30); setSelected(null); }}>30 {copy(language, 'dk', 'min')}</button>
            <button type="button" className={duration === 60 ? 'is-active' : ''} onClick={() => { setDuration(60); setSelected(null); }}>60 {copy(language, 'dk', 'min')}</button>
          </div>
        </aside>
        <section className="meeting-page__slots" aria-labelledby="slots-title">
          <div className="meeting-page__slotshead"><div><span>{copy(language, 'Uygun saatler', 'Available times')}</span><h2 id="slots-title">{copy(language, 'Size uygun zamanı seçin', 'Choose a time that works for you')}</h2></div>{selected && <Button variant="primary" loading={book.isPending} onClick={() => book.mutate()}>{copy(language, 'Randevuyu onayla', 'Confirm appointment')}</Button>}</div>
          {availability.isLoading ? <LoadingBlock rows={5} label={copy(language, 'Uygun saatler yükleniyor', 'Loading available times')} /> : availability.isError ? <ErrorState message={copy(language, 'Uygun saatler alınamadı.', 'Available times could not be loaded.')} onRetry={() => void availability.refetch()} /> : !availability.data?.providerReady ? <div className="meeting-page__notice"><h3>{copy(language, 'Takvim bağlantısı hazırlanıyor', 'Calendar connection is being prepared')}</h3><p>{copy(language, 'Google Workspace takvimi henüz yapılandırılmamış. Telefonla görüşme seçeneğini kullanabilirsiniz.', 'Google Workspace Calendar has not been configured yet. You can choose a phone call instead.')}</p><Link to="/">{copy(language, 'Ana sayfaya dön', 'Back to home')}</Link></div> : grouped.length === 0 ? <div className="meeting-page__notice"><h3>{copy(language, 'Bu aralıkta uygun saat yok', 'No available times in this range')}</h3><p>{copy(language, 'Daha sonra tekrar kontrol edin veya telefonla görüşme isteyin.', 'Check again later or request a phone call.')}</p></div> : (
            <div className="meeting-days">
              {grouped.map(([day, slots]) => <section className="meeting-day" key={day}><header><strong>{formatDay(day, locale)}</strong><span>{slots.length} {copy(language, 'seçenek', 'options')}</span></header><div>{slots.map((slot) => <button type="button" className={selected?.startsAt === slot.startsAt ? 'is-active' : ''} key={slot.startsAt} onClick={() => setSelected(slot)}>{new Date(slot.startsAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' })}</button>)}</div></section>)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function groupSlots(slots: Slot[]): Array<[string, Slot[]]> {
  const map = new Map<string, Slot[]>();
  for (const slot of slots) {
    const day = new Date(new Date(slot.startsAt).getTime() + 3 * 60 * 60_000).toISOString().slice(0, 10);
    map.set(day, [...(map.get(day) ?? []), slot]);
  }
  return [...map.entries()];
}

function formatDay(day: string, locale: 'tr-TR' | 'en-US'): string {
  return new Date(`${day}T12:00:00+03:00`).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Istanbul' });
}
